import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

const MAX_BYTES = 8 * 1024 * 1024;
const UPLOAD_TIMEOUT_MS = 120000;
const URL_TIMEOUT_MS = 30000;

function sanitizeFileName(name) {
  return (name || 'image').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
}

function guessImageMime(name) {
  const n = (name || '').toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  return '';
}

function resolveImageContentType(file) {
  if (file.type && file.type.startsWith('image/')) return file.type;
  const guessed = guessImageMime(file.name);
  if (guessed) return guessed;
  return '';
}

function withTimeout(promise, ms, message) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([
    promise.finally(() => {
      clearTimeout(timer);
    }),
    timeoutPromise,
  ]).finally(() => {
    clearTimeout(timer);
  });
}

/**
 * Görseli Firebase Storage'a yükler (tek uploadBytes isteği — resumable WebChannel bazen takılır).
 */
export async function uploadWatchImage(file, options = {}) {
  const { onProgress } = options;

  const contentType = resolveImageContentType(file);
  if (!contentType.startsWith('image/')) {
    throw new Error('Lütfen bir resim dosyası seçin (JPG, PNG, WebP, GIF).');
  }
  if (file.size === 0) {
    throw new Error('Dosya boş görünüyor; başka bir dosya seçin.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Dosya boyutu en fazla 8 MB olabilir.');
  }

  onProgress?.(8);

  const baseName = sanitizeFileName(file.name);
  const path = `watches/${Date.now()}_${Math.random().toString(36).slice(2, 10)}_${baseName}`;
  const storageRef = ref(storage, path);

  const metadata = { contentType };

  onProgress?.(22);

  await withTimeout(
    uploadBytes(storageRef, file, metadata),
    UPLOAD_TIMEOUT_MS,
    'Yükleme zaman aşımı (2 dk). Firebase Console’da Storage etkin mi, Rules’ta watches/ yazma izni var mı kontrol edin. Ağ/VPN engeli olabilir.'
  );

  onProgress?.(88);

  const url = await withTimeout(
    getDownloadURL(storageRef),
    URL_TIMEOUT_MS,
    'Yükleme tamamlandı ama indirme adresi alınamadı. Bir süre sonra tekrar deneyin.'
  );

  onProgress?.(100);
  return url;
}
